import { Category } from '@smg-real-estate/hg-rets-schema';

export enum TopCategoryGroup {
  RESIDENTIAL = 'RESIDENTIAL',
  COMMERCIAL = 'COMMERCIAL',
  PARKING = 'PARKING',
}

// A collection of similar Category's
export enum TopCategory {
  AGRI = 'AGRI',
  APPT = 'APPT',
  GASTRO = 'GASTRO',
  HOUSE = 'HOUSE',
  INDUS = 'INDUS',
  PARK = 'PARK',
  PROP = 'PROP',
  SECONDARY = 'SECONDARY',
}

export const topCategoryGroups: Record<TopCategoryGroup, TopCategory[]> = {
  [TopCategoryGroup.RESIDENTIAL]: [
    TopCategory.APPT,
    TopCategory.HOUSE,
    TopCategory.PROP,
    TopCategory.SECONDARY,
  ],
  [TopCategoryGroup.COMMERCIAL]: [TopCategory.GASTRO, TopCategory.AGRI, TopCategory.INDUS],
  [TopCategoryGroup.PARKING]: [TopCategory.PARK],
};

export const topCategories: Record<TopCategory, Category[]> = {
  [TopCategory.APPT]: [
    Category.Apartment,
    Category.Duplex,
    Category.AtticFlat,
    Category.RoofFlat,
    Category.Studio,
    Category.SingleRoom,
    Category.FurnishedFlat,
    Category.TerraceFlat,
    Category.BachelorFlat,
    Category.Loft,
    Category.Attic,
  ],
  [TopCategory.HOUSE]: [
    Category.SingleHouse,
    Category.RowHouse,
    Category.BifamiliarHouse,
    Category.TerraceHouse,
    Category.Villa,
    Category.FarmHouse,
    Category.MultipleDwelling,
    Category.Castle,
    Category.GrannyFlat,
    Category.Chalet,
    Category.RusticHouse,
  ],
  [TopCategory.PROP]: [Category.BuildingLand, Category.CommercialLand],
  [TopCategory.SECONDARY]: [Category.HobbyRoom, Category.CellarCompartment],
  [TopCategory.GASTRO]: [
    Category.Hotel,
    Category.Restaurant,
    Category.Coffeehouse,
    Category.Bar,
    Category.ClubDisco,
    Category.Pub,
  ],
  [TopCategory.AGRI]: [Category.AgriculturalInstallation],
  [TopCategory.INDUS]: [
    Category.Office,
    Category.Shop,
    Category.Commercial,
    Category.StorageRoom,
    Category.Practice,
    Category.Kiosk,
    Category.Garage,
    Category.Hairdresser,
    Category.ShoppingCentre,
    Category.Factory,
    Category.IndustrialObject,
    Category.Arcade,
    Category.Atelier,
    Category.ResidentialCommercialBuilding,
    Category.Workshop,
    Category.DepartmentStore,
    Category.ParkingSpace,
  ],
  [TopCategory.PARK]: [
    Category.OpenSlot,
    Category.CoveredSlot,
    Category.SingleGarage,
    Category.DoubleGarage,
    Category.UndergroundSlot,
    Category.CoveredParkingPlaceBike,
  ],
};

export const topCategoriesOrdered: Record<TopCategory, Category[]> = {
  [TopCategory.APPT]: [
    Category.Apartment,
    Category.RoofFlat,
    Category.AtticFlat,
    Category.FurnishedFlat,
    Category.Duplex,
    Category.TerraceFlat,
    Category.Studio,
    Category.SingleRoom,
    Category.BachelorFlat,
    Category.Loft,
  ],
  [TopCategory.HOUSE]: [
    Category.SingleHouse,
    Category.RowHouse,
    Category.BifamiliarHouse,
    Category.MultipleDwelling,
    Category.Villa,
    Category.FarmHouse,
    Category.Chalet,
    Category.RusticHouse,
    Category.TerraceHouse,
    Category.Castle,
  ],
  [TopCategory.PROP]: [Category.BuildingLand, Category.CommercialLand],
  [TopCategory.SECONDARY]: [
    Category.HobbyRoom,
    Category.AtticCompartment,
    Category.CellarCompartment,
  ],
  [TopCategory.GASTRO]: [
    Category.Restaurant,
    Category.Bar,
    Category.Hotel,
    Category.Coffeehouse,
    Category.ClubDisco,
  ],
  [TopCategory.AGRI]: [Category.AgriculturalInstallation],
  [TopCategory.INDUS]: [
    Category.Office,
    Category.Shop,
    Category.Commercial,
    Category.StorageRoom,
    Category.Practice,
    Category.IndustrialObject,
    Category.Atelier,
    Category.Workshop,
    Category.Hairdresser,
    Category.ResidentialCommercialBuilding,
    Category.Arcade,
    Category.DepartmentStore,
    Category.Kiosk,
    Category.Factory,
    Category.ShoppingCentre,
  ],
  [TopCategory.PARK]: [
    Category.UndergroundSlot,
    Category.OpenSlot,
    Category.SingleGarage,
    Category.CoveredSlot,
  ],
};

export const categoriesForTopCategory = (topCategory: TopCategory): Category[] =>
  topCategories[topCategory] ?? [];

export const topCategoryForCategories = (categories: Category[]): TopCategory | undefined => {
  for (const category of categories.slice().reverse()) {
    for (const [topCategory, subCategories] of Object.entries(topCategories)) {
      // eslint-disable-next-line max-depth
      if (subCategories.includes(category)) {
        return topCategory as TopCategory;
      }
    }
  }
  return undefined;
};

const categoriesForTopCategoryGroup = (topCategoryGroup: TopCategoryGroup): Category[] =>
  topCategoryGroups[topCategoryGroup].flatMap((topCategory) => topCategories[topCategory] ?? []);

export const residentialCategories: Category[] = categoriesForTopCategoryGroup(
  TopCategoryGroup.RESIDENTIAL,
);

export const commercialCategories: Category[] = categoriesForTopCategoryGroup(
  TopCategoryGroup.COMMERCIAL,
);
